import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Store,
  Handshake,
  Package,
  Tag,
  ShoppingCart,
  DollarSign,
  Settings,
  HelpCircle,
  LogOut,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
  isTablet: boolean;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const mainNavItems: NavItem[] = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/marketplace', label: 'Marketplace', icon: Store },
  { path: '/negotiations', label: 'Negotiations', icon: Handshake },
  { path: '/inventory', label: 'Inventory', icon: Package },
  { path: '/offers', label: 'Offers', icon: Tag },
  { path: '/requests', label: 'Requests', icon: ShoppingCart },
  { path: '/finances', label: 'Finances', icon: DollarSign },
];

/**
 * Sidebar component - Left navigation sidebar
 * 
 * Features:
 * - Logo at top (icons only)
 * - Navigation items with icons only (no text labels)
 * - Settings and Help/Logout at bottom
 * - Active route highlighting
 * - Responsive: always visible on desktop (64px width), overlay on mobile/tablet
 */
export const Sidebar = ({ isOpen, onClose, isMobile, isTablet }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleNavClick = () => {
    // Close sidebar on mobile/tablet when navigating
    if (isMobile || isTablet) {
      onClose();
    }
  };

  const handleLogout = async () => {
    await logout();
    handleNavClick();
    navigate('/auth/login');
  };

  // Determine sidebar classes based on screen size and state
  const getSidebarClasses = () => {
    if (isMobile) {
      // Mobile: fixed overlay, full-screen
      return 'fixed inset-y-0 left-0 z-50 bg-background border-r border-border flex flex-col transition-transform duration-300 ease-in-out w-full ' + 
             `${isOpen ? 'translate-x-0' : '-translate-x-full'}`;
    } else if (isTablet) {
      // Tablet: fixed overlay, 280px width
      return 'fixed inset-y-0 left-0 z-50 bg-background border-r border-border flex flex-col transition-transform duration-300 ease-in-out w-[280px] ' + 
             `${isOpen ? 'translate-x-0' : '-translate-x-full'}`;
    } else {
      // Desktop: fixed (stays visible on scroll), always visible, icons-only (64px width)
      return 'fixed inset-y-0 left-0 z-50 bg-background border-r border-border flex flex-col w-16';
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      data-sidebar
      className={getSidebarClasses()}
    >
      {/* Logo */}
      <div className={`h-16 flex items-center ${isMobile || isTablet ? 'px-6' : 'justify-center px-2'} border-b border-border`}>
        <Link to="/" onClick={handleNavClick} className="flex items-center gap-2">
          <span className="text-h2 font-bold text-primary">iATM</span>
          {(isMobile || isTablet) && <span className="text-base text-text-secondary">Marketplace</span>}
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <ul className={`space-y-1 ${isMobile || isTablet ? 'px-4' : 'px-2'}`}>
          {mainNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={handleNavClick}
                  className={`
                    flex items-center ${isMobile || isTablet ? 'gap-4' : 'justify-center'} ${isMobile || isTablet ? 'px-4' : 'px-2'} py-2 rounded-md transition-colors
                    ${active
                      ? 'bg-primary-light text-primary font-medium'
                      : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
                    }
                  `}
                  title={item.label}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {(isMobile || isTablet) && <span className="text-base">{item.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom Section: Settings, Help, Logout */}
      <div className="border-t border-border py-4">
        <ul className={`space-y-1 ${isMobile || isTablet ? 'px-4' : 'px-2'}`}>
          <li>
            <Link
              to="/settings"
              onClick={handleNavClick}
              className={`
                flex items-center ${isMobile || isTablet ? 'gap-4' : 'justify-center'} ${isMobile || isTablet ? 'px-4' : 'px-2'} py-2 rounded-md transition-colors
                ${isActive('/settings')
                  ? 'bg-primary-light text-primary font-medium'
                  : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
                }
              `}
              title="Settings"
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {(isMobile || isTablet) && <span className="text-base">Settings</span>}
            </Link>
          </li>
          <li>
            <Link
              to="/help"
              onClick={handleNavClick}
              className={`flex items-center ${isMobile || isTablet ? 'gap-4' : 'justify-center'} ${isMobile || isTablet ? 'px-4' : 'px-2'} py-2 rounded-md text-text-secondary hover:bg-background-secondary hover:text-text-primary transition-colors`}
              title="Help"
            >
              <HelpCircle className="w-5 h-5 flex-shrink-0" />
              {(isMobile || isTablet) && <span className="text-base">Help</span>}
            </Link>
          </li>
          <li>
            <button
              onClick={handleLogout}
              className={`w-full flex items-center ${isMobile || isTablet ? 'gap-4' : 'justify-center'} ${isMobile || isTablet ? 'px-4' : 'px-2'} py-2 rounded-md text-text-secondary hover:bg-background-secondary hover:text-text-primary transition-colors`}
              title="Logout"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {(isMobile || isTablet) && <span className="text-base">Logout</span>}
            </button>
          </li>
        </ul>
      </div>
    </aside>
  );
};

